import type { InferGetServerSidePropsType, GetServerSideProps } from 'next'
import { useLiveQuery } from '~/lib/sanity-live-query'

import Footer from '../../components/Footer';
import Layout from '../../components/Layout';
import { getGlobalData } from '../../utils/global-data';
import SEO from '../../components/SEO';
import StickyNavbar from '../../components/StickyNavbar';
import Gallery from '../../components/Gallery';
import { PortableText } from '@portabletext/react'

import { readToken } from '~/lib/sanity.api'
import { getClient } from '~/lib/sanity.client'
import { getPage, getInspirations, getSiteConfig, pageBySlugQuery, inspirationsQuery, siteConfigQuery } from '~/lib/sanity.queries'
import { type Page, Inspiration, SiteConfig } from '~/lib/sanity.interfaces'
import type { SharedPageProps } from '~/pages/_app'

interface Query {
  [key: string]: string
}

export const getServerSideProps: GetServerSideProps<
  SharedPageProps & {
    page: Page,
    inspirations: Inspiration[],
    siteConfig: SiteConfig,
  },
  Query
> = async ({ draftMode = false, params = {} }) => {
  const client = getClient(draftMode ? { token: readToken } : undefined)
  const page = await getPage(client, 'inspiration')
  const siteConfig = await getSiteConfig(client)
  const inspirations = await getInspirations(client)

  if (!page) {
    return {
      notFound: true,
    }
  }
  const globalData = getGlobalData();

  return {
    props: {
      globalData,
      draftMode,
      token: draftMode ? readToken : '',
      page,
      inspirations,
      siteConfig,
    },
  }
}

export default function Index(props: InferGetServerSidePropsType<typeof getServerSideProps>,
  globalData
) {
  const [page] = useLiveQuery(props.draftMode, props.page, pageBySlugQuery, {
    slug: props.page.slug.current,
  })
  const [siteConfig] = useLiveQuery(props.draftMode, props.siteConfig, siteConfigQuery)
  const [inspirations] = useLiveQuery<Inspiration[]>(props.draftMode, props.inspirations, inspirationsQuery)

  return (
    <Layout>
      <SEO title={page.seoTitle} description={page.seoDescription} keywords={page.seoKeywords} />
      <StickyNavbar siteConfig={siteConfig} />
      <main className="w-full bg-background nav-offset py-4 lg:py-8">  
        <h1 className="text-4xl lg:text-5xl text-right mb-12 mr-10">
          {page.title}
        </h1>
        <div className='text-2xl lg:text-2xl text-right mb-12 mr-10 ml-10 md:ml-40 contentBlock'>
          <PortableText value={page.body} />
        </div>
        <div className='mx-0 sm:mx-10'>
          <Gallery items={inspirations} prefix="/inspiration/" addFill={true} />
        </div>
      </main>
      <Footer siteConfig={siteConfig} />      
    </Layout>
  );
}

